import type { CSSProperties, ReactNode } from "react";

import { styled } from "simplydo/react/styled";
import { Container } from "simplydo/react/ui/container";

export const EntityHeaderShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: ${({ theme }) => (theme.sizes.isMobile ? 20 : 32)}px;
  color: #111418;
  width: 100%;
  box-sizing: border-box;
`;

export const EntityHeaderOverlap = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  gap: ${({ theme }) => (theme.sizes.isMobile ? 16 : 28)}px;
  margin-top: ${({ theme }) => (theme.sizes.isMobile ? -48 : -80)}px;
  align-items: flex-start;
`;

export const EntityHeaderMain = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: ${({ theme }) => (theme.sizes.isMobile ? 0 : 96)}px;
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "stretch" : "flex-end"};
  justify-content: space-between;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 16 : 32)}px;
`;

export const EntityHeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: ${({ theme }) => (theme.sizes.isMobile ? 28 : 36)}px;
    line-height: 1.15;
    font-weight: 650;
    letter-spacing: -0.02em;
    color: #111418;
  }
`;

export const EntityHeaderTagline = styled.p`
  margin: 0;
  font-size: 17px;
  color: #5b6470;
`;

export const EntityHeaderMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: 10px;
  font-size: 14px;
  color: #3a414b;

  a {
    color: inherit;
    font-weight: 600;
  }
`;

export const EntityHeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex: none;
  align-items: center;
  justify-content: flex-end;

  &.is-mobile {
    width: 100%;
    justify-content: flex-start;
  }
`;

/** @deprecated Prefer EntityHeaderMain / EntityHeaderOverlap. Kept for wizard imports. */
export const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "flex-end"};
  &.is-preview {
    padding: 0 2rem;
  }
  margin-top: 3px;
`;

/** @deprecated Prefer EntityHeaderTitles. Kept for wizard imports, which render on a dark banner overlay. */
export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  &.is-mobile {
    margin-top: 5px;
  }
  width: 100%;
  h1,
  h5,
  span:not(.dropdown .text) {
    margin: 0;
    color: white;
  }
  .titles {
    margin-bottom: 1rem;
    .meta {
      display: block;
      font-size: 1.1em;
      margin-bottom: 2.5px;
    }
  }
  &.has-user .owner {
    margin-bottom: 1rem;
  }
  .owner {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: white;
  }
  .info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .sector {
    text-transform: uppercase;
  }
`;

/** @deprecated Prefer EntityHeaderActions. */
export const HeaderActions = EntityHeaderActions;

export const StyledContainer = styled(Container)`
  &.is-computer {
    padding: 20px 0 30px;
    &.is-fluid {
      padding: 1rem 3rem;
    }
  }
  &.is-mobile {
    padding: 10px 0;
  }
`;

export const EntityMetaSectionLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5b6470;
`;

export const EntityMetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const EntityMetaGrid = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 20 : 32)}px;

  > ${EntityMetaBlock}:first-child {
    flex: 1 1 0;
    min-width: 0;
  }
`;

export const EntityMetaSide = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  flex-wrap: wrap;
  align-items: flex-start;
  flex: 0 1 50%;
  max-width: ${({ theme }) => (theme.sizes.isMobile ? "100%" : "50%")};
  min-width: 0;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 20 : 24)}px;

  ${EntityMetaBlock} {
    flex: 1 1 120px;
    min-width: 0;
    max-width: 100%;
  }
`;

export const EntityMetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  width: 100%;
  min-width: 0;

  & > div {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-width: 0;
    max-width: 100%;
  }
`;

export const EntityPeopleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

type EntityHeaderContentProps = {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  fluid?: boolean;
};

const EntityHeaderContent = ({
  children,
  style,
  className,
  fluid,
}: EntityHeaderContentProps) => {
  return (
    <EntityHeaderShell style={style} className={className}>
      {fluid ? children : <Container>{children}</Container>}
    </EntityHeaderShell>
  );
};

export default EntityHeaderContent;
